import { RootState } from '@/store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type TotalTrafficAccidents = {
  totalAccidents: number
  totalAccidentsInjuries: number
  totalAccidentsDead: number
  withoutInjuries: number
  withInjuries: number
  withInjuriesInjuries: number
  fatals: number
  fatalInjuries: number
  fatalsDead: number
  preClusters: number
  preClustersInjuries: number
  preClustersDead: number
  clusters: number
  clustersInjuries: number
  clustersDead: number
}

export type TotalLighting = {
  totalLightingCurrent: number
  totalLightingCurrentLength: number
  totalLightingCurrentLuminaires: number
  totalLightingCurrentPoles: number
  totalLightingCurrentPower: number
  totalLightingOther: number
  totalLightingOtherLength: number
  totalLightingOtherLuminaires: number
  totalLightingOtherPoles: number
  totalLightingOtherPower: number
  totalLightingPerspective: number
  totalLightingPerspectiveLength: number
  totalLightingPerspectiveLuminaires: number
  totalLightingPerspectivePoles: number
  totalLightingPerspectivePower: number
}

export type DevicesCount = {
  totalErrors: number
  totalModulesCount: number
  totalNotSmart: number
  totalPhotoCamera: number
  totalServiceable: number
  totalSmart: number
  totalSmartLampCountErrors: number
  totalSmartLampCountSuccess: number
  totalSmartTurnCountErrors: number
  totalSmartTurnCountSuccess: number
  totalVideoCamera: number
  totalWarnings: number
  totalLighting: number
  totalDit: number
  totalWeatherStation: number
  totalRoad: number
  totalTrafficLight: number
  totalLightingCurrent: number
  totalLightingPerspective: number
  totals: {
    totalTrafficDetector: number
    totalTrafficAccidents: TotalTrafficAccidents
    totalLighting: TotalLighting
  }
}

const initialState: { counters: DevicesCount } = {
  counters: {
    totalErrors: 0,
    totalWarnings: 0,
    totalModulesCount: 0,
    totalNotSmart: 0,
    totalPhotoCamera: 0,
    totalServiceable: 0,
    totalSmart: 0,
    totalSmartLampCountErrors: 0,
    totalSmartLampCountSuccess: 0,
    totalSmartTurnCountErrors: 0,
    totalSmartTurnCountSuccess: 0,
    totalVideoCamera: 0,
    totalLighting: 0,
    totalDit: 0,
    totalWeatherStation: 0,
    totalRoad: 0,
    totalTrafficLight: 0,
    totalLightingCurrent: 0,
    totalLightingPerspective: 0,
    totals: {
      totalTrafficDetector: 0,
      totalTrafficAccidents: {
        totalAccidents: 0,
        totalAccidentsInjuries: 0,
        totalAccidentsDead: 0,
        withoutInjuries: 0,
        withInjuries: 0,
        withInjuriesInjuries: 0,
        fatals: 0,
        fatalInjuries: 0,
        fatalsDead: 0,
        preClusters: 0,
        preClustersInjuries: 0,
        preClustersDead: 0,
        clusters: 0,
        clustersInjuries: 0,
        clustersDead: 0,
      },
      totalLighting: {
        totalLightingCurrent: 0,
        totalLightingCurrentLength: 0,
        totalLightingCurrentLuminaires: 0,
        totalLightingCurrentPoles: 0,
        totalLightingCurrentPower: 0,
        totalLightingOther: 0,
        totalLightingOtherLength: 0,
        totalLightingOtherLuminaires: 0,
        totalLightingOtherPoles: 0,
        totalLightingOtherPower: 0,
        totalLightingPerspective: 0,
        totalLightingPerspectiveLength: 0,
        totalLightingPerspectiveLuminaires: 0,
        totalLightingPerspectivePoles: 0,
        totalLightingPerspectivePower: 0,
      },
    },
  },
}

export const defaultFooterTotals: DevicesCount['totals'] = initialState.counters.totals

export function mergeFooterTotals(
  incoming?: Partial<{
    totalTrafficDetector: number
    totalTrafficAccidents: Partial<TotalTrafficAccidents>
    totalLighting: Partial<TotalLighting>
  }> | null
): DevicesCount['totals'] {
  return {
    totalTrafficDetector:
      incoming?.totalTrafficDetector ?? defaultFooterTotals.totalTrafficDetector,
    totalTrafficAccidents: {
      ...defaultFooterTotals.totalTrafficAccidents,
      ...incoming?.totalTrafficAccidents,
    },
    totalLighting: {
      ...defaultFooterTotals.totalLighting,
      ...incoming?.totalLighting,
    },
  }
}

const footerSlice = createSlice({
  name: 'footerSlice',
  initialState,
  reducers: {
    setCounters: (state, action: PayloadAction<Partial<DevicesCount>>) => {
      const p = action.payload
      state.counters = {
        ...state.counters,
        ...p,
        totals:
          p.totals !== undefined && p.totals !== null
            ? mergeFooterTotals(p.totals)
            : state.counters.totals,
      }
    },
  },
})

export const footerSelector = (state: RootState) =>
  state.footerSliceReducer.counters

export const footerSliceReducer = footerSlice.reducer
export const footerSliceActions = footerSlice.actions
